var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { shouldCacheResponse } from '../util/cache-predicate';
import { Header } from '../util/headers';
import { updateCache } from '../util/update-cache';
import { setupCacheData } from './util';
export function defaultResponseInterceptor(axios) {
    /**
     * Rejects cache for an response response.
     *
     * Also update the waiting list for this key by rejecting it.
     */
    const rejectResponse = ({ storage, waiting }, responseId) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        // Update the cache to empty to prevent infinite loading state
        yield storage.remove(responseId);
        // Reject the deferred if present
        (_a = waiting[responseId]) === null || _a === void 0 ? void 0 : _a.reject(null);
        delete waiting[responseId];
    });
    const onFulfilled = (response) => __awaiter(this, void 0, void 0, function* () {
        var _b, _c;
        (_b = response.id) !== null && _b !== void 0 ? _b : (response.id = axios.generateKey(response.config));
        (_c = response.cached) !== null && _c !== void 0 ? _c : (response.cached = false);
        // Response is already cached
        if (response.cached) {
            return response;
        }
        // Skip cache: either false or weird behavior
        // config.cache should always exists, at least from global config merge.
        if (!response.config.cache) {
            return Object.assign(Object.assign({}, response), { cached: false });
        }
        const cacheConfig = response.config.cache;
        const cache = yield axios.storage.get(response.id);
        if (
        // If the request interceptor had a problem
        cache.state === 'stale' ||
            cache.state === 'empty' ||
            // Should not hit here because of previous response.cached check
            cache.state === 'cached') {
            return response;
        }
        // Config told that this response should be cached.
        if (
        // For 'loading' values (post stale), this check was already run in the past.
        !cache.data &&
            !shouldCacheResponse(response, cacheConfig)) {
            yield rejectResponse(axios, response.id);
            return response;
        }
        // avoid remnant headers from remote server to break implementation
        delete response.headers[Header.XAxiosCacheEtag];
        delete response.headers[Header.XAxiosCacheLastModified];
        if (cacheConfig.etag && cacheConfig.etag !== true) {
            response.headers[Header.XAxiosCacheEtag] = cacheConfig.etag;
        }
        if (cacheConfig.modifiedSince) {
            response.headers[Header.XAxiosCacheLastModified] =
                cacheConfig.modifiedSince === true
                    ? 'use-cache-timestamp'
                    : cacheConfig.modifiedSince.toUTCString();
        }
        let ttl = cacheConfig.ttl || -1; // always set from global config
        if (cacheConfig === null || cacheConfig === void 0 ? void 0 : cacheConfig.interpretHeader) {
            const expirationTime = axios.headerInterpreter(response.headers);
            // Cache should not be used
            if (expirationTime === 'dont cache') {
                yield rejectResponse(axios, response.id);
                return response;
            }
            ttl = expirationTime === 'not enough headers' ? ttl : expirationTime;
        }
        const data = setupCacheData(response, cache.data);
        if (typeof ttl === 'function') {
            ttl = yield ttl(response);
        }
        const newCache = {
            state: 'cached',
            ttl,
            createdAt: Date.now(),
            data
        };
        // Update other entries before updating himself
        if (cacheConfig === null || cacheConfig === void 0 ? void 0 : cacheConfig.update) {
            updateCache(axios.storage, response, cacheConfig.update);
        }
        const deferred = axios.waiting[response.id];
        // Resolve all other requests waiting for this response
        yield (deferred === null || deferred === void 0 ? void 0 : deferred.resolve(newCache.data));
        delete axios.waiting[response.id];
        // Define this key as cache on the storage
        yield axios.storage.set(response.id, newCache);
        // Return the response with cached as false, because it was not cached at all
        return response;
    });
    return {
        onFulfilled,
        apply: () => axios.interceptors.response.use(onFulfilled)
    };
}
